import type { FunctionComponent } from 'react';
import { useMemo, useState } from 'react';

import { intl } from '@trello/i18n';
import { smallestPreview } from '@trello/image-previews';
import { makeTemplateUrlSlug } from '@trello/urls';

import { SearchForm } from 'app/src/components/Navigation/SearchForm';
import { createSearchIndex } from './SearchHelper';
import type { Template } from './types';

import styles from './SearchWrapper.less';

interface SearchWrapperProps {
  templates?: Template[];
}

export const SearchWrapper: FunctionComponent<SearchWrapperProps> = ({
  templates,
}) => {
  const [searchValue, setSearchValue] = useState('');
  templates = templates || [];

  const searchIndex = useMemo(() => createSearchIndex(templates), [templates]);
  const searchResults: Template[] = searchIndex.search(searchValue);

  const results = searchResults.map(
    ({ shortLink, name, templateGallery, prefs }) => {
      const smallBackground = smallestPreview(prefs.backgroundImageScaled);
      const iconUrl = smallBackground
        ? smallBackground.url
        : prefs.backgroundImage
          ? prefs.backgroundImage
          : '';

      return {
        label: name || '',
        value: {
          identifier: makeTemplateUrlSlug(
            templateGallery.category,
            name,
            shortLink,
          ),
          iconUrl,
          backgroundColor: prefs.backgroundColor || '',
          subtext: intl
            .formatMessage(
              {
                id: 'templates.templates.by',
                defaultMessage: 'by {author}',
                description: 'The author of the template',
              },
              {
                author: templateGallery.byline,
              },
            )
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&'),
        },
      };
    },
  );

  return (
    <div className={styles.searchWrapper} role="searchbox">
      <SearchForm
        placeholder={intl.formatMessage({
          id: 'templates.templates.find-template',
          defaultMessage: 'Find template',
          description: 'A search query to find a specific template',
        })}
        results={results}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        // We do not pass a searchUrlSlug because there is no search results page for templates
        resultUrlSlug={'templates'}
      />
    </div>
  );
};
